<template>
  <v-form
    class="custom-form"
    validate-on="submit"
    @submit.prevent="onSubmit"
  >
    <v-text-field
      v-model="email"
      label="Email"
      variant="outlined"
      class="custom-input"
      autocomplete="email"
      :rules="[r.required, r.email]"
      hide-details="auto"
      @keyup.enter="onSubmit"
    />

    <v-btn
      :loading="loading"
      :disabled="loading"
      class="custom-button"
      type="submit"
      block
    >
      Send Reset Link
    </v-btn>

    <v-btn
      variant="text"
      class="center-link"
      @click="$emit('back')"
    >
      Back to login
    </v-btn>
  </v-form>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { usePasswordReset } from '@/interfaces/composables/auth/usePasswordReset';

  const emit = defineEmits<{
    (e: 'back'): void;
    (e: 'sent'): void;
    (
      e: 'notify',
      payload: { text: string; color: 'success' | 'error' | 'info' }
    ): void;
  }>();

  const { sendResetEmail, loading, error, success } = usePasswordReset();

  const email = ref('');

  const r = {
    required: (v: string) => !!v || 'Required',
    email: (v: string) => /.+@.+\..+/.test(v) || 'Invalid email',
  };

  async function onSubmit() {
    if (loading.value) return;
    if (!email.value) return;
    await sendResetEmail(email.value);

    if (success.value) {
      emit('notify', {
        text: 'Reset link sent. Check your email.',
        color: 'success',
      });
      emit('sent');
    } else if (error.value) {
      emit('notify', { text: error.value, color: 'error' });
    }
  }

  watch(error, (msg) => {
    if (msg) emit('notify', { text: msg, color: 'error' });
  });
</script>

<style scoped lang="sass">
  .custom-form
    width: 100%
    height: 100%
    display: flex
    flex-direction: column

  .custom-input
    margin-bottom: 24px
    width: 100%
    :deep(.v-field)
      border-radius: 10px
      --v-field-border-color: #ccc
      width: 100%

  .custom-button
    border: 2px solid #1976d2
    background-color: transparent
    color: #1976d2
    font-weight: 400
    font-size: 16px
    height: 50px
    border-radius: 10px
    margin-top: 32px
    text-transform: none
    transition: all 0.2s ease
    &:hover
      background-color: rgba(25, 118, 210, 0.04)
      border-color: #1565c0
      color: #1565c0

  .center-link
    align-self: center
    margin-top: 12px

  :deep(.v-input.v-input--error)
    .v-label, .v-field__outline, .v-messages__message
      color: #f57c00 !important
      border-color: #f57c00 !important

  :deep(.v-input.v-input--error .v-field--focused .v-field__outline)
    border-color: #f57c00 !important
    box-shadow: 0 0 0 2px rgba(245,124,0,0.15) !important
</style>
